import React from "react";
import { ViewModeToggle, ViewMode, ActionButton } from "../../shared";

interface AdminPageHeaderProps {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  buttonText?: string;
  onButtonClick?: () => void;
  buttonDisabled?: boolean;
  buttonLoading?: boolean;
  // View mode toggle
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  showViewToggle?: boolean;
  // Additional controls
  midleContent?: React.ReactNode;
  rightContent?: React.ReactNode;
  /** Full-width row below the header (e.g. primary actions / CTAs). */
  ctaStrip?: React.ReactNode;
}

export const AdminPageHeader: React.FC<AdminPageHeaderProps> = ({
  icon,
  title,
  subtitle,
  buttonText,
  onButtonClick,
  buttonDisabled = false,
  buttonLoading = false,
  viewMode,
  onViewModeChange,
  showViewToggle = false,
  midleContent,
  rightContent,
  ctaStrip,
}) => {
  return (
    <div className="bg-white dark:bg-[#2D3748] rounded-xl sm:rounded-2xl shadow-medium border border-gray-100 dark:border-[#374151] p-4 sm:p-5 md:p-6">
      <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3 sm:gap-4">
        {/* Top Row: Icon, Title, Subtitle */}
        <div className="flex items-center gap-2 sm:gap-3 md:gap-4 min-w-0 flex-1">
          <span className="flex-shrink-0 flex items-center justify-center [&>.MuiSvgIcon-root]:text-2xl sm:[&>.MuiSvgIcon-root]:text-2xl md:[&>.MuiSvgIcon-root]:text-3xl text-gray-600 dark:text-slate-400">
            {icon}
          </span>
          <h2 className="text-xl sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-white truncate">{title}</h2>
          <p className="text-sm sm:text-sm md:text-base text-gray-600 dark:text-slate-400 truncate hidden sm:block">{subtitle}</p>
        </div>
        {/* Show subtitle on mobile as separate line */}
        <p className="text-sm text-gray-600 dark:text-slate-400 sm:hidden">{subtitle}</p>

        {/* Bottom Row: All Controls */}
        <div className="flex flex-col xl:flex-row items-stretch xl:items-center gap-2 sm:gap-2 md:gap-3 flex-shrink-0 w-full xl:w-auto">
          {/* Main Controls: View Toggle, Middle Content, Action Button */}
          <div className="flex items-center gap-2 sm:gap-2 md:gap-3 flex-wrap xl:flex-nowrap">
            {/* View Mode Toggle */}
            {showViewToggle && viewMode && onViewModeChange && (
              <ViewModeToggle
                viewMode={viewMode}
                onViewModeChange={onViewModeChange}
                className="flex-shrink-0"
              />
            )}

            {midleContent}
            {/* Create/Action Button */}
            {buttonText && onButtonClick && (
              <ActionButton
                onClick={onButtonClick}
                disabled={buttonDisabled}
                loading={buttonLoading}
                variant="primary"
                size="sm"
                icon={buttonLoading ? "⏳" : "+"}
                className="flex-shrink-0 whitespace-nowrap"
              >
                {buttonText}
              </ActionButton>
            )}
          </div>

          {/* Additional Right Content - Separate line on mobile/tablet/large, inline on xl */}
          {rightContent && (
            <div className="flex items-center gap-2 sm:gap-2 md:gap-3 flex-shrink-0 w-full xl:w-auto">
              {rightContent}
            </div>
          )}
        </div>
      </div>

      {ctaStrip && (
        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-[#374151]">
          {ctaStrip}
        </div>
      )}
    </div>
  );
};
